import React, { useCallback, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { Button } from '@this/src/components/shared/ui/inputs/button';
import Notification from '@this/src/notification';
import { Fetcher } from '@this/src/util';

interface Props {
  onClose: () => void;
  onUploaded: () => void;
}

interface CheckDuplicateResponse {
  has_duplicates: boolean;
  duplicate_dates: string[];
}

interface CreateUploadResponse {
  success: boolean;
  upload_action_id: number;
  error?: string;
}

interface UploadActionShowResponse {
  upload_action: {
    id: number;
    status: string;
    original_filename: string | null;
    record_count: number;
    error: string | null;
    finished_at: string | null;
  };
}

const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 30 * 60 * 1000;

const sleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

const UploadModal: React.FC<Props> = ({ onClose, onUploaded }) => {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [processingMessage, setProcessingMessage] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const styles = useMemo(() => getStyles(isDragOver), [isDragOver]);

  const pollUploadAction = useCallback(
    async (actionId: number) => {
      const deadline = Date.now() + POLL_TIMEOUT_MS;
      while (Date.now() < deadline) {
        await sleep(POLL_INTERVAL_MS);
        try {
          const res = await Fetcher.get<UploadActionShowResponse>(
            `/arrangement/auto_reservation_inventory/ean_hotel/upload/${actionId}.json`
          );
          const { status, record_count, error } = res.upload_action;
          if (status === 'succeeded') {
            Notification.success(`アップロードが完了しました（${record_count}件）`);
            onUploaded();
            onClose();
            return;
          }
          if (status === 'failed') {
            Notification.error(error || 'アップロードに失敗しました');
            return;
          }
        } catch {
          // 単発エラーは継続してリトライ（タイムアウトで打ち切られる）
        }
      }
      Notification.error('アップロード状況の確認がタイムアウトしました。時間をおいて画面を更新してください。');
    },
    [onClose, onUploaded]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.name.endsWith('.csv')) {
      setFile(droppedFile);
    } else {
      Notification.error('CSVファイルを選択してください');
    }
  }, []);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  }, []);

  const handleUpload = useCallback(async () => {
    if (!file) return;

    setLoading(true);
    setProcessingMessage(null);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const checkRes = await Fetcher.upload<CheckDuplicateResponse>(
        '/arrangement/auto_reservation_inventory/ean_hotel/upload/check_duplicate',
        formData
      );

      if (checkRes.has_duplicates) {
        const dates = checkRes.duplicate_dates.join(', ');
        const confirmed = window.confirm(`以下の日付のデータが既に登録されています。続行しますか？\n\n${dates}`);
        if (!confirmed) {
          setLoading(false);
          return;
        }
      }

      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const uploadRes = await Fetcher.upload<CreateUploadResponse>(
        '/arrangement/auto_reservation_inventory/ean_hotel/upload',
        uploadFormData
      );

      if (uploadRes.success) {
        setProcessingMessage('アップロードを開始しました。処理の完了を待っています…');
        await pollUploadAction(uploadRes.upload_action_id);
      } else {
        Notification.error(uploadRes.error || 'アップロードに失敗しました');
      }
    } catch {
      Notification.error('アップロードに失敗しました');
    } finally {
      setLoading(false);
      setProcessingMessage(null);
    }
  }, [file, pollUploadAction]);

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h3 style={styles.modalTitle}>EANベースレポートCSVアップロード</h3>
          <button style={styles.closeButton} onClick={onClose}>
            &times;
          </button>
        </div>
        <div style={styles.modalBody}>
          <div
            style={styles.dropZone}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            {file ? (
              <p style={styles.fileName}>{file.name}</p>
            ) : (
              <p style={styles.dropZoneText}>
                CSVファイルをドラッグ＆ドロップ
                <br />
                またはクリックしてファイルを選択
              </p>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              style={styles.hiddenInput}
            />
          </div>
          <p style={styles.description}>
            CSVには ITINERARY_NUMBER、TRANSACTION_TYPE、BOOK_DATE 列が必要です。
            <br />
            TRANSACTION_TYPE が Purchase の行のみ取り込まれます。
          </p>
          {processingMessage && <p style={styles.processingMessage}>{processingMessage}</p>}
        </div>
        <div style={styles.modalFooter}>
          <Button onClick={onClose} disabled={loading}>
            キャンセル
          </Button>
          <Button onClick={handleUpload} disabled={!file || loading} loading={loading}>
            {loading ? '処理中…' : 'アップロード'}
          </Button>
        </div>
      </div>
    </div>
  );
};

const getStyles = (isDragOver: boolean) => ({
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  } as CSSProperties,
  modal: {
    background: 'white',
    borderRadius: 8,
    width: 500,
    maxWidth: '90%',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
  } as CSSProperties,
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    borderBottom: '1px solid #eee'
  } as CSSProperties,
  modalTitle: {
    margin: 0,
    fontSize: 16
  } as CSSProperties,
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: 24,
    cursor: 'pointer',
    color: '#666',
    padding: 0,
    lineHeight: 1
  } as CSSProperties,
  modalBody: {
    padding: 20
  } as CSSProperties,
  dropZone: {
    border: `2px dashed ${isDragOver ? '#007bff' : '#ccc'}`,
    borderRadius: 8,
    padding: '40px 20px',
    textAlign: 'center',
    cursor: 'pointer',
    background: isDragOver ? '#f0f7ff' : '#fafafa',
    transition: 'all 0.2s'
  } as CSSProperties,
  dropZoneText: {
    color: '#666',
    margin: 0,
    lineHeight: 1.6
  } as CSSProperties,
  fileName: {
    color: '#333',
    fontWeight: 'bold',
    margin: 0
  } as CSSProperties,
  hiddenInput: {
    display: 'none'
  } as CSSProperties,
  description: {
    fontSize: 12,
    color: '#888',
    marginTop: 12,
    lineHeight: 1.6
  } as CSSProperties,
  processingMessage: {
    fontSize: 12,
    color: '#007bff',
    marginTop: 12,
    lineHeight: 1.6
  } as CSSProperties,
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
    padding: '16px 20px',
    borderTop: '1px solid #eee'
  } as CSSProperties
});

export default UploadModal;
